import Grid from '@mui/material/Grid2';
import { AutoComplete, Card, Typography } from '@utilisourcepackagelibdev/utilisourcepackagelib';
import TextBox from '@/components/Molecules/Textbox/Textbox.component';
import {
  Box,
  Button,
  Chip,
  List,
  ListItem,
  Stack,
  Tooltip,
  useMediaQuery,
  Theme,
  CircularProgress,
  Alert,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { createCustomerOptionsQuery } from '@/services/customer/customer.options';
import { CustomerEntity } from '@/interfaces/customer.interface';
import CheckboxInput from '@/components/Atoms/Checkbox/CheckboxInput.component';
import useTicketTypes from '@/services/type/type.options';
import { TicketType } from '@/interfaces/ticketType.interface';
import useUserProfile, { createAllUsersQueryOptions } from '@/services/users/userProfile.options';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller, SubmitHandler, FormProvider } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState } from 'react';
import { PasswordSchema } from '@/interfaces/schemas/users.schema';

const UserProfile = ({ agentContext }: { agentContext: any }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const theme = useTheme();
  const { ticketTypesByAllCustomers } = useTicketTypes();

  const customers = useQuery(createCustomerOptionsQuery());
  const customerData = customers?.data;
  const ticketTypeData = ticketTypesByAllCustomers?.data;
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const { executeUserProfileAction, executeUserProfileActionMutation } = useUserProfile(
    agentContext?.ContextUser?.AgentId,
  );
  const allUsers = useQuery(createAllUsersQueryOptions());
  const currentUser = allUsers?.data?.find((user: any) => user.agentid === agentContext?.ContextUser?.AgentId);
  const usersWithCustomersAndTypes = allUsers?.data?.filter(
    (user: any) => user.CustomerList !== null && user.TypesList !== null,
  );

  const methods = useForm<z.infer<typeof PasswordSchema>>({
    mode: 'all',
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    resolver: zodResolver(PasswordSchema),
  });
  const { control, watch, setValue } = methods;
  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');

  const handleSubmit: SubmitHandler<any> = (formValues: any) => {
    setIsUpdating(true);
    executeUserProfileActionMutation.mutate(
      {
        actionType: 'resetPassword',
        payload: {
          agentId: agentContext?.ContextUser?.AgentId,
          password: formValues.newPassword.trim(),
        },
      },
      {
        onSuccess: (response: any) => {
          setIsUpdating(false);
          setValue('newPassword', '');
          setValue('confirmPassword', '');
        },
        onError: (error: any) => {
          setIsUpdating(false);
        },
      },
    );
  };

  return (
    <Grid
      fullWidth
      sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 5 }}>
      {!currentUser ? (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            mt: 2,
          }}>
          <CircularProgress />
          <p>Loading </p>
        </Box>
      ) : (
        <>
          <Alert variant="filled" severity="info" sx={{ color: theme.palette.background.paper, backgroundColor: theme.palette.primary.main }}>
            The only editable field on this page is the password. All other information is read-only. If you would like
            to change any of this information, please contact your administrator.
          </Alert>

          <Card sx={{ p: 5 }}>
            <Stack direction="column" spacing={5}>
              <Stack direction={isDesktop ? 'row' : 'column'} spacing={5}>
                <TextField
                  id="user-name"
                  label="Name"
                  value={currentUser?.name || 'N/A'}
                  fullWidth
                  disabled
                  sx={{
                    flex: 1,
                  }}
                />
                <TextField
                  id="user-role"
                  label="Role"
                  value={currentUser?.rolename || 'N/A'}
                  fullWidth
                  disabled
                  sx={{
                    flex: 1,
                  }}
                />
              </Stack>
              <Stack direction={isDesktop ? 'row' : 'column'} spacing={5}>
                <TextField
                  id="user-phone"
                  label="Phone"
                  value={currentUser?.phone || 'N/A'}
                  fullWidth
                  disabled
                  sx={{
                    flex: 1,
                  }}
                />
                <TextField
                  id="user-email"
                  label="Email"
                  value={currentUser?.email || 'N/A'}
                  fullWidth
                  disabled
                  sx={{
                    flex: 1,
                  }}
                />
              </Stack>
            </Stack>
          </Card>
          <Stack direction={isDesktop ? 'row' : 'column'} spacing={5}>
            <Card sx={{ flex: 1, p: 5 }}>
              <Stack direction={isDesktop ? 'row' : 'column'} spacing={5} alignItems="center">
                <Typography variant="h4">Linked Customers</Typography>
                {currentUser?.CustomerList === null && (
                  <Chip color="primary" variant="outlined" label="No customers linked to this user" />
                )}
              </Stack>
              <List>
                {customerData?.map((customer: CustomerEntity) => {
                  const isChecked = currentUser?.CustomerList?.some(
                    (c: any) => c.CustomerID === customer.CustomerEntId,
                  );
                  return (
                    <ListItem sx={{ p: 0, m: 0 }} key={customer.CustomerEntId}>
                      <CheckboxInput
                        sxProps={{ p: 1, m: 0 }}
                        id={customer.EntName}
                        label={customer.EntName}
                        checked={isChecked}
                        disabled={true}
                        onDataChange={() => { }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Card>
            <Card sx={{ flex: 1, p: 5 }}>
              <Stack direction={isDesktop ? 'row' : 'column'} spacing={5} alignItems="center">
                <Typography variant="h4">Linked Ticket Types</Typography>
                {currentUser?.TypesList === null && (
                  <Chip color="primary" variant="outlined" label="No ticket types linked to this user" />
                )}
              </Stack>
              <List>
                {ticketTypeData?.map((ticketType: TicketType) => {
                  const isChecked = currentUser?.TypesList?.some((t: any) => t.TypeID === ticketType.TypeID);
                  return (
                    <ListItem sx={{ p: 0, m: 0 }} key={ticketType.TypeID}>
                      <CheckboxInput
                        sxProps={{ p: 1, m: 0 }}
                        id={ticketType.TypeName}
                        label={ticketType.TypeName}
                        checked={isChecked}
                        disabled={true}
                        onDataChange={() => { }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Card>
          </Stack>
          <Card sx={{ p: 5 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h4">Reset Password</Typography>
                <Tooltip placement="right" title="Fill out the fields below to change this user's password.">
                  <InfoOutlinedIcon sx={{ color: theme.palette.primary.main }} />
                </Tooltip>
              </Box>

              <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleSubmit)}>
                  <Stack direction={isDesktop ? 'row' : 'column'} spacing={2} sx={{ pt: 5 }}>
                    <TextBox
                      size="small"
                      id="newPassword"
                      name="newPassword"
                      label="New Password"
                      control={control}
                      type="password"
                      sx={{ flex: 1 }}
                    />
                    <TextBox
                      size="small"
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Confirm Password"
                      control={control}
                      type="password"
                      sx={{ flex: 1 }}
                    />
                    <Box>
                      <LoadingButton
                        loading={isUpdating}
                        variant="contained"
                        color="primary"
                        type="submit"
                        onClick={() => {
                          methods.handleSubmit((data) => {
                            handleSubmit(data);
                          })();
                        }}>
                        Reset Password
                      </LoadingButton>
                    </Box>
                  </Stack>
                </form>
              </FormProvider>
            </Box>
          </Card>
        </>
      )}
    </Grid>
  );
};

export default UserProfile;
